import { Component, OnInit } from '@angular/core';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { PreChatService } from '../services/pre-chat.service';
import { DisplaySetting } from '../models/display-setting';
import { SkillAvailability } from '../models/skill-availability';
import { CHAT_TYPE } from '../models/chat-type-model';

@Component({
    selector: 'pre-chat-root',
    templateUrl: './pre-chat.component.html',
    styleUrls: ['./pre-chat.component.scss']
})
export class PreChatComponent implements OnInit {

    reCaptchaEnabled: boolean = model.recaptchaEnabled;
    reCaptchaSiteKey: string = model.reCaptchaSiteKey;
    error: string = model.error;
    language: string = model.userLanguage;
    formTag: string;
    formType: CHAT_TYPE;
    timezone: string = "";
    displaySettingId: number = parseInt(model.displaySettingId);
    resources = resources;
    model = model;
    skillAvailabilityList: SkillAvailability[];
    settings: any = null;
    form: any;
    chatSessionId: string;
    location:Location;
    skillAvailabilityCheckTimer: any;

    constructor(private preChatService: PreChatService) {
        this.preChatService.activeChatData$.subscribe(activeChatData => {
            var formBefore = { formTag: this.formTag, location: this.location, formType: this.formType, chatSessionId: this.chatSessionId };

            if (activeChatData.formTag)
                this.formTag = activeChatData.formTag;
            else
                this.formTag = null;

            if (activeChatData.location) {
                this.location = activeChatData.location;
            }

            this.formType = activeChatData.formType;
            this.chatSessionId = activeChatData.chatSessionId;
            var formAfter = { formTag: this.formTag, location: this.location, formType: this.formType, chatSessionId: this.chatSessionId };
            // reloading form destroys previous one, do only when needed         
            if (JSON.stringify(formBefore) != JSON.stringify(formAfter)) {
                this.loadForm();
            }
        });
    }

    ngOnInit(): void {
        this.language = this.language || window.navigator.language || "en";
        this.timezone = this.preChatService.getTimeZone();
        this.preChatService.sendChatFrameLoadEvent();
    }

    loadForm() {
        this.settings = null;
        let form$: Observable<any> = this.preChatService.getPreChatForm(this.language, this.formTag).pipe(
            catchError(err => {
                console.error(`Failed to load pre chat form with language: '${this.language}' and formTag:'${this.formTag}' `);
                return throwError(err);
            }),
            tap(() => console.log("Successfully fetched pre chat form"))
        );
        let displaySetting$: Observable<DisplaySetting> = this.preChatService.getDisplaySetting(this.displaySettingId).pipe(
            catchError(err => {
                console.error(`Failed to load display setting with id: '${this.displaySettingId}'`);
                return throwError(err);
            }),
            tap(() => console.log("Successfully fetched display setting"))
        );

        let defaultSkillAddress$: Observable<string> = this.preChatService.getDefaultSkillAddress().pipe(
            catchError(err => {
                console.error('Failed to get default skill address');
                return throwError(err);
            }),
            tap(() => console.log("Successfully fetched default skill address"))
        );

        forkJoin(form$, displaySetting$, defaultSkillAddress$).subscribe({
            next: data => {
                let [form, displaySettings, defaultSkillAddress] = data;
                let skillAddresses: string[];

                if (this.model.overriddenSkillAddresses) {
                    // Override skill addresses used for availability check
                    skillAddresses = this.model.overriddenSkillAddresses.split(',');
                } else if (this.model.overriddenSkillAddresses === '') {
                    // Skip availability check (with empty array)
                    skillAddresses = [];
                } else {
                    // No override specified, fall back to configuration
                    // Get list of skill addresses configured in the pre-chat form
                    let skillField = form.rows.find(row => row.fieldTypeName === 'Skill Dropdown' && row.mappingFieldName === 'Source');
                    // Use drop down options OR default skill address
                    skillAddresses = skillField ? skillField.options.map(option => option.valueCode) :
                        [this.model.targetSkillAddress || defaultSkillAddress];
                }
                let me = this;

                this.skillAvailabilityCheckTimer = setInterval(function() {
                    me.checkSkillAvailability(skillAddresses);
                }, 15 * 1000);

                this.form = form;
                this.settings = {
                    form,
                    displaySettings,
                    "formTag": this.formTag,
                    "formType": this.formType,
                    "chatSessionId": this.chatSessionId,
                    "sourceUrl":this.location.pathname
                };
                if (this.formType === CHAT_TYPE.REACTIVE) {
                    if (this.formTag !== null && form.tag !== this.formTag) {
                        console.info("Unable to find form with tag:-" + this.formTag + " so loading default form with Id 1");
                    }
                    //TODO Handle scenario when error occurs on submitting Reactive chat incase there is no form
                    if (this.formTag === null && this.error) {
                        model.error = this.error = null;
                        console.info("Error occured while creating reactive chat");
                    }

                    if (this.formTag === null) {
                        this.preChatService.hideChat();
                    }
                }
                /// Invoking skill Availability check here ensures that logic for show/hide chat button runs even if
                /// 1. FormTag was set to null in chat snippet
                /// 2. check-availability is set to an empty array in chat snippet.
                me.checkSkillAvailability(skillAddresses);
                if (this.error && this.formType === CHAT_TYPE.PROACTIVE && form.tag !== this.formTag) {
                    //Send error response to proactive engine for displaying error when no form is provided for Proactive
                    this.preChatService.sendProactiveErrorMessage(this.error);
                    //since error will be handled by proactive engine hence setting it null here
                    model.error = this.error = null;
                }
            },
            error: err => {
                console.error(err);
            }
        });
    }

    checkSkillAvailability(skillAddresses: string[]): void {
        if (skillAddresses.length === 0) {
            this.preChatService.enableChat();
            return;
        }

        this.preChatService.getSkillStatus(skillAddresses).subscribe({
            next: data => {
                this.skillAvailabilityList = data;
                if (this.skillAvailabilityList.some(skillAvailability => skillAvailability.Status === 'open')) {
                    this.preChatService.enableChat(); // Enable chat if any skills are open
                } else {
                    this.preChatService.hideChat(); // Hide chat if any skills are close
                }
            }
        });
    }

    chatFormSubmitted($event): void {
        console.info('form submitted, and clear the skill availability check timer!, event : ' + $event);
        if ($event) {
            clearInterval(this.skillAvailabilityCheckTimer);
        }
    }
}
